/**
 * useATClient — manages the Africa's Talking WebRTC SDK lifecycle.
 *
 * Responsibilities:
 *  - Initialises the AT SDK with a capability token.
 *  - Subscribes to SDK events and tracks call state (outbound + inbound).
 *  - Schedules a proactive token refresh 10 minutes before expiry and
 *    re-initialises the SDK without interrupting any active call.
 *  - Auto-reconnects on offline/closed events and when the tab regains focus.
 *  - Derives a network quality signal from SDK lifecycle events and
 *    navigator.onLine so the UI can show a meaningful indicator.
 *
 * The hook does NOT own the token — the caller (App) fetches the initial token
 * and supplies a `refreshToken` callback for the scheduled refresh.
 */

import Africastalking, { type ATClientInstance } from "africastalking-client";
import { useCallback, useEffect, useRef, useState } from "react";

// ─── Types ────────────────────────────────────────────────────────────────────

/**
 * Snapshot of a call that just ended, used to drive the post-call wrap-up screen.
 * Set in the hangup handler BEFORE call state is cleared so the screen has all
 * the context it needs.
 */
export interface LastEndedCallInfo {
  direction: "inbound" | "outbound";
  /** E.164 number of the other party. */
  remoteNumber: string;
  /** ISO timestamp when the rep answered; null if the call was never answered. */
  answeredAt: string | null;
  /** ISO timestamp when the call ended (hangup fired). */
  endedAt: string;
  contactName: string | null;
  contactId: string | null;
  contactTags: string[];
  /**
   * DB id of the calls row. Present for inbound calls (broadcast from server);
   * null for outbound (call row is created asynchronously by the AT webhook so
   * the dialer doesn't know it yet — the wrap-up endpoint falls back to a
   * scoped "most-recent outbound call" query in that case).
   */
  callId: string | null;
}

/** Context pushed from the server via Supabase Realtime when an inbound call arrives. */
export interface IncomingCallInfo {
  /** DB id of the calls row — used by the wrap-up endpoint for a precise update. */
  callId: string | null;
  /** AT session ID — used to correlate with the AT SDK incomingcall event. */
  sessionId: string;
  /** Caller's E.164 phone number. */
  callerNumber: string;
  /** Contact name if the caller is in the contact book, otherwise null. */
  contactName: string | null;
  /** Contact DB id, or null for unknown callers. */
  contactId: string | null;
  /** Tags attached to the contact. */
  contactTags: string[];
  /** The rep this call was originally assigned to, if any. */
  assignedRepId: string | null;
}

export type SdkStatus =
  | "idle" // not yet initialised
  | "initialising" // SDK constructor called, waiting for "ready"
  | "ready" // ready to make/receive calls
  | "ringing" // call ringing (outbound: waiting for answer; inbound: waiting for rep to pick up)
  | "incall" // call answered, audio active
  | "notready" // SDK connected but not accepting calls
  | "offline" // token expired / connection lost
  | "closed"; // connection closed (network drop, tab sleep)

export type NetworkQuality = "good" | "degraded" | "poor" | "unknown";

export interface ActiveCallInfo {
  /** E.164 number being called (outbound) or caller (inbound). */
  remoteNumber: string;
  /** ISO timestamp when the call was answered (null while ringing). */
  answeredAt: string | null;
  /** Whether this is an inbound or outbound call — stored here so it survives
   *  incomingCall being cleared on answer(). */
  direction: "inbound" | "outbound";
  /** DB id of the calls row. Set for inbound (from Realtime broadcast) and for
   *  outbound once the server broadcasts it back after creating the row. */
  callId: string | null;
  /** Contact fields carried over from IncomingCallInfo (inbound) or resolved
   *  after mount (outbound). Stored here so hangup can populate lastEndedCall. */
  contactName: string | null;
  contactId: string | null;
  contactTags: string[];
}

export interface ATClientState {
  sdkStatus: SdkStatus;
  networkQuality: NetworkQuality;
  /** Non-null when SDK init failed and calls are unavailable. */
  initError: string | null;
  activeCall: ActiveCallInfo | null;
  /** Non-null while an inbound call is ringing, waiting for the rep to answer or decline. */
  incomingCall: IncomingCallInfo | null;
  /** Populated when a call ends; consumed by PostCallScreen, then cleared via clearLastEndedCall. */
  lastEndedCall: LastEndedCallInfo | null;
  isMuted: boolean;
  isOnHold: boolean;
}

export interface ATClientActions {
  call: (phoneNumber: string) => void;
  /** Answer the current inbound call. */
  answer: () => void;
  hangup: () => void;
  mute: () => void;
  unmute: () => void;
  hold: () => void;
  unhold: () => void;
  /** Re-attempt SDK initialisation after an error. */
  retryInit: () => void;
  /** Merge rich context (contact name, tags, etc.) from the Supabase Realtime broadcast. */
  setIncomingCallContext: (ctx: IncomingCallInfo) => void;
  /** Clear the last-ended-call snapshot (called by App after PostCallScreen is done). */
  clearLastEndedCall: () => void;
  /** Called by App when the server broadcasts the DB call id for an outbound call. */
  setOutboundCallId: (callId: string) => void;
}

// ─── Constants ────────────────────────────────────────────────────────────────

/** Refresh 10 minutes before the token expires. */
const REFRESH_BEFORE_EXPIRY_MS = 10 * 60 * 1000;
/** Wait before attempting auto-reconnect (gives the network time to settle). */
const RECONNECT_DELAY_MS = 1_500;

// ─── Hook ─────────────────────────────────────────────────────────────────────

interface UseATClientOptions {
  /** Initial capability token. Null means "not yet fetched". */
  token: string | null;
  /** Lifetime in seconds returned by the API (typically 86 400). */
  expiresIn: number;
  /** Called when a proactive refresh is needed; must return a new token. */
  refreshToken: () => Promise<string | null>;
  /**
   * Called the moment a rep clicks Answer on an inbound call, with the AT
   * session ID.  App uses this to fire a server call that sets profile_id on
   * the call row immediately — more reliable than extracting it from AT's
   * webhook destinationNumber field.
   */
  onInboundAnswered?: (sessionId: string) => void;
}

export function useATClient({
  token,
  expiresIn,
  refreshToken,
  onInboundAnswered,
}: UseATClientOptions): ATClientState & ATClientActions {
  const [sdkStatus, setSdkStatus] = useState<SdkStatus>("idle");
  const [networkQuality, setNetworkQuality] = useState<NetworkQuality>("unknown");
  const [initError, setInitError] = useState<string | null>(null);
  const [activeCall, setActiveCall] = useState<ActiveCallInfo | null>(null);
  const [incomingCall, setIncomingCall] = useState<IncomingCallInfo | null>(null);
  const [lastEndedCall, setLastEndedCall] = useState<LastEndedCallInfo | null>(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isOnHold, setIsOnHold] = useState(false);

  const clientRef = useRef<ATClientInstance | null>(null);
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const currentTokenRef = useRef<string | null>(null);
  const sdkStatusRef = useRef<SdkStatus>("idle");
  // Generation counter: incremented on every initSDK call so that event
  // handlers registered by an old ATClientInstance become no-ops once a new
  // client is created (e.g. after switching outbound numbers).
  const generationRef = useRef(0);
  // Track whether a call is active so proactive refresh and auto-reconnect skip re-init.
  const isInCallRef = useRef(false);
  const pendingRefreshToken = useRef<string | null>(null);

  // Ref mirrors of call state — kept up-to-date so the hangup handler and
  // defensive hangup action can read the latest values synchronously before
  // React has a chance to flush the null-clearing state updates.
  const incomingCallRef = useRef<IncomingCallInfo | null>(null);
  useEffect(() => {
    incomingCallRef.current = incomingCall;
  }, [incomingCall]);

  const activeCallRef = useRef<ActiveCallInfo | null>(null);
  useEffect(() => {
    activeCallRef.current = activeCall;
  }, [activeCall]);

  // Flag: set to true when the defensive hangup action already captured the
  // call snapshot so the SDK event handler doesn't overwrite it.
  const hangupCapturedRef = useRef(false);

  // Always-current ref for refreshToken — avoids stale closure in the auto-reconnect timer.
  const refreshTokenRef = useRef(refreshToken);
  useEffect(() => {
    refreshTokenRef.current = refreshToken;
  }, [refreshToken]);

  // ── Derive network quality from SDK status + navigator.onLine ──────────────

  const deriveNetworkQuality = useCallback((status: SdkStatus): NetworkQuality => {
    if (!navigator.onLine) return "poor";
    if (status === "ready" || status === "incall" || status === "ringing") return "good";
    if (status === "initialising" || status === "notready") return "degraded";
    if (status === "offline" || status === "closed") return "poor";
    return "unknown";
  }, []);

  const updateStatus = useCallback(
    (status: SdkStatus) => {
      sdkStatusRef.current = status;
      setSdkStatus(status);
      setNetworkQuality(deriveNetworkQuality(status));
      isInCallRef.current = status === "ringing" || status === "incall";
    },
    [deriveNetworkQuality],
  );

  // ── Schedule proactive refresh ─────────────────────────────────────────────

  const scheduleRefresh = useCallback(
    (lifetimeSeconds: number) => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);

      const delayMs = Math.max(0, lifetimeSeconds * 1000 - REFRESH_BEFORE_EXPIRY_MS);

      refreshTimerRef.current = setTimeout(() => {
        void (async () => {
          const newToken = await refreshToken();
          if (!newToken) return;

          if (isInCallRef.current) {
            // Don't re-init mid-call — queue it for after hangup.
            pendingRefreshToken.current = newToken;
          } else {
            initSDK(newToken);
          }

          // Schedule next refresh.
          scheduleRefresh(lifetimeSeconds);
        })();
      }, delayMs);
    },
    [refreshToken],
  );

  // ── Auto-reconnect ref — set after initSDK is defined (see useEffect below) ─
  // Using a ref breaks the circular dependency: initSDK → scheduleAutoReconnect → initSDK.
  const scheduleAutoReconnectRef = useRef<() => void>(() => {});

  // ── Core SDK initialisation ────────────────────────────────────────────────

  const initSDK = useCallback(
    async (capToken: string) => {
      if (capToken === currentTokenRef.current) return; // already initialised with this token
      currentTokenRef.current = capToken;

      // Cancel any pending auto-reconnect timer.
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }

      // Bump the generation BEFORE the async wait so any concurrent initSDK call
      // during the teardown window will get a higher generation and supersede us.
      const generation = ++generationRef.current;
      const isCurrentGen = () => generation === generationRef.current;

      setInitError(null);
      updateStatus("initialising");

      // ── Tear down the old session ──────────────────────────────────────────
      //
      // The AT SDK reuses module-level state (registration flag) across Client
      // instances. Calling logout() unregisters the old session so the server
      // sends back "unregistered" and clears that flag before the new Client
      // sends its own "register" request.
      //
      // A short fixed delay (400 ms) gives the server time to process the
      // unregister response. This is far simpler than awaiting specific events
      // and avoids re-triggering the auto-reconnect loop while we wait.
      if (clientRef.current) {
        try {
          clientRef.current.logout();
        } catch {
          // Socket may already be closed — ignore and continue with re-init.
        }
        await new Promise<void>((resolve) => setTimeout(resolve, 400));
        if (!isCurrentGen()) return; // superseded while we were waiting
      }

      let client: ATClientInstance;
      try {
        client = new Africastalking.Client(capToken);
      } catch (err) {
        if (!isCurrentGen()) return;
        const msg = err instanceof Error ? err.message : "Unknown error";
        setInitError(`SDK failed to start: ${msg}`);
        updateStatus("offline");
        return;
      }

      clientRef.current = client;

      client.on("ready", () => {
        if (!isCurrentGen()) return;
        updateStatus("ready");
        setInitError(null);
      });

      client.on("notready", () => {
        if (!isCurrentGen()) return;
        updateStatus("notready");
      });

      client.on("calling", () => {
        if (!isCurrentGen()) return;
        updateStatus("ringing");
      });

      client.on("callaccepted", () => {
        if (!isCurrentGen()) return;
        updateStatus("incall");
        // Only set answeredAt if not already set (outbound calls).
        // For inbound, answer() already set it precisely when the rep clicked Answer.
        setActiveCall((prev) =>
          prev ? { ...prev, answeredAt: prev.answeredAt ?? new Date().toISOString() } : prev,
        );
      });

      // incomingcall fires when AT routes an inbound call to this browser client.
      // params.from is the caller's number (set via callerId in our <Dial> XML).
      // Rich context (contactName, tags, etc.) arrives separately via Supabase
      // Realtime and is merged in via setIncomingCallContext().
      client.on("incomingcall", (params) => {
        if (!isCurrentGen()) return;
        setIncomingCall((prev) => ({
          // Preserve any Realtime context that arrived before the AT event.
          callId: prev?.callId ?? null,
          sessionId: prev?.sessionId ?? "",
          callerNumber: (params.from as string | undefined) ?? prev?.callerNumber ?? "",
          contactName: prev?.contactName ?? null,
          contactId: prev?.contactId ?? null,
          contactTags: prev?.contactTags ?? [],
          assignedRepId: prev?.assignedRepId ?? null,
        }));
        updateStatus("ringing");
      });

      client.on("hangup", () => {
        if (!isCurrentGen()) return;

        // Capture call snapshot for PostCallScreen — only if the defensive
        // hangup action didn't already do it (remote party hung up first).
        if (!hangupCapturedRef.current) {
          const wasActive = activeCallRef.current;
          const wasIncoming = incomingCallRef.current;
          // Missed inbound = rep never answered (incomingCall set, activeCall never set).
          const missedInbound = !wasActive && !!wasIncoming;
          // wasActive carries direction/callId/contact for ALL answered calls —
          // inbound info was copied into it by answer(), outbound set by call().
          if (!missedInbound && wasActive) {
            setLastEndedCall({
              direction: wasActive.direction,
              remoteNumber: wasActive.remoteNumber,
              answeredAt: wasActive.answeredAt,
              endedAt: new Date().toISOString(),
              contactName: wasActive.contactName,
              contactId: wasActive.contactId,
              contactTags: wasActive.contactTags,
              callId: wasActive.callId,
            });
          }
        }
        hangupCapturedRef.current = false; // reset for next call

        updateStatus("ready");
        setActiveCall(null);
        setIncomingCall(null);
        setIsMuted(false);
        setIsOnHold(false);

        // Apply any pending token refresh that was deferred during the call.
        if (pendingRefreshToken.current) {
          const queued = pendingRefreshToken.current;
          pendingRefreshToken.current = null;
          void initSDK(queued);
        }
      });

      // On offline/closed: auto-reconnect silently instead of prompting a page refresh.
      client.on("offline", () => {
        if (!isCurrentGen()) return;
        updateStatus("offline");
        if (!isInCallRef.current) scheduleAutoReconnectRef.current();
      });

      client.on("closed", () => {
        if (!isCurrentGen()) return;
        updateStatus("closed");
        if (!isInCallRef.current) scheduleAutoReconnectRef.current();
      });
    },
    [updateStatus],
  );

  // ── Wire up auto-reconnect (after initSDK is defined) ─────────────────────

  useEffect(() => {
    scheduleAutoReconnectRef.current = () => {
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      // Show "reconnecting" state while we wait.
      updateStatus("initialising");
      reconnectTimerRef.current = setTimeout(() => {
        void (async () => {
          const newToken = await refreshTokenRef.current();
          if (newToken) {
            currentTokenRef.current = null; // force re-init with fresh token
            void initSDK(newToken);
          } else {
            // Token refresh failed — surface "offline" so the user sees the
            // manual Reconnect button rather than being stuck in "initialising".
            updateStatus("offline");
          }
        })();
      }, RECONNECT_DELAY_MS);
    };
  }, [initSDK, updateStatus]);

  // ── Re-connect when the tab becomes visible again ─────────────────────────
  //
  // Chrome can throttle or freeze background tabs, causing the AT SDK's
  // WebSocket to drop.  When the tab regains focus we skip the normal
  // RECONNECT_DELAY_MS and reconnect immediately — the user just switched
  // back to this tab so the network is fine.

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible" && !isInCallRef.current) {
        const s = sdkStatusRef.current;
        if (s === "offline" || s === "closed") {
          // Cancel any pending delayed reconnect and go immediately.
          if (reconnectTimerRef.current) {
            clearTimeout(reconnectTimerRef.current);
            reconnectTimerRef.current = null;
          }
          updateStatus("initialising");
          void (async () => {
            const newToken = await refreshTokenRef.current();
            if (newToken) {
              currentTokenRef.current = null; // force re-init with fresh token
              void initSDK(newToken);
            } else {
              updateStatus("offline");
            }
          })();
        }
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [initSDK, updateStatus]);

  // ── Watch for network changes ──────────────────────────────────────────────

  useEffect(() => {
    const handleOnline = () => {
      setNetworkQuality(deriveNetworkQuality(sdkStatusRef.current));
      // If we went offline and are now back online, trigger a reconnect.
      const s = sdkStatusRef.current;
      if (s === "offline" || s === "closed") {
        scheduleAutoReconnectRef.current();
      }
    };
    const handleOffline = () => setNetworkQuality("poor");
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [deriveNetworkQuality]);

  // ── Initialise when the token arrives ─────────────────────────────────────

  useEffect(() => {
    if (!token) return;
    void initSDK(token);
    scheduleRefresh(expiresIn);
    return () => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    };
  }, [token]);

  // ── Actions ───────────────────────────────────────────────────────────────

  const call = useCallback((phoneNumber: string) => {
    const client = clientRef.current;
    if (!client) return;
    setActiveCall({
      remoteNumber: phoneNumber,
      answeredAt: null,
      direction: "outbound",
      callId: null,
      contactName: null,
      contactId: null,
      contactTags: [],
    });
    client.call(phoneNumber);
  }, []);

  /** Answer the current inbound call. */
  const answer = useCallback(() => {
    clientRef.current?.answer();
    const inc = incomingCallRef.current;
    if (inc) {
      // Copy ALL context from incomingCall into activeCall BEFORE clearing it.
      // This is critical — incomingCall is nulled out below, so by the time
      // hangup fires, activeCallRef is the only source of truth for direction,
      // callId, and contact info.
      setActiveCall({
        remoteNumber: inc.callerNumber,
        answeredAt: new Date().toISOString(),
        direction: "inbound",
        callId: inc.callId,
        contactName: inc.contactName,
        contactId: inc.contactId,
        contactTags: inc.contactTags,
      });
      // Notify server immediately so profile_id is written to the call row now.
      if (inc.sessionId) {
        onInboundAnswered?.(inc.sessionId);
      }
    }
    setIncomingCall(null);
    updateStatus("incall");
  }, [updateStatus, onInboundAnswered]);

  const hangup = useCallback(() => {
    const client = clientRef.current;
    if (!client) return;

    // Capture snapshot BEFORE clearing state so PostCallScreen has context.
    // The SDK will also fire a hangup event — use the flag to prevent it from
    // overwriting this snapshot (values would be identical but better to be safe).
    const wasActive = activeCallRef.current;
    const wasIncoming = incomingCallRef.current;
    const missedInbound = !wasActive && !!wasIncoming;
    if (!missedInbound && wasActive) {
      hangupCapturedRef.current = true;
      setLastEndedCall({
        direction: wasActive.direction,
        remoteNumber: wasActive.remoteNumber,
        answeredAt: wasActive.answeredAt,
        endedAt: new Date().toISOString(),
        contactName: wasActive.contactName,
        contactId: wasActive.contactId,
        contactTags: wasActive.contactTags,
        callId: wasActive.callId,
      });
    }

    client.hangup();
    // Defensively reset state in case SDK doesn't fire hangup event.
    updateStatus("ready");
    setActiveCall(null);
    setIncomingCall(null);
    setIsMuted(false);
    setIsOnHold(false);
  }, [updateStatus]);

  const mute = useCallback(() => {
    clientRef.current?.muteAudio();
    setIsMuted(true);
  }, []);

  const unmute = useCallback(() => {
    clientRef.current?.unmuteAudio();
    setIsMuted(false);
  }, []);

  /** Hold: mutes the local microphone so the other party hears silence.
   *
   * The AT SDK's hold() / unhold() methods attempt a SIP re-INVITE which
   * disrupts the existing WebSocket signaling channel and causes "error
   * occured on session creation" + "WebSocket is already in CLOSING or
   * CLOSED state" errors. Local mute achieves the same user-visible result
   * (the other party can't hear the agent) without touching the session.
   */
  const hold = useCallback(() => {
    clientRef.current?.muteAudio();
    setIsMuted(true);
    setIsOnHold(true);
  }, []);

  const unhold = useCallback(() => {
    clientRef.current?.unmuteAudio();
    setIsMuted(false);
    setIsOnHold(false);
  }, []);

  const retryInit = useCallback(() => {
    if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
    currentTokenRef.current = null; // force re-init
    void refreshTokenRef.current().then((t) => t && void initSDK(t));
  }, [initSDK]);

  const clearLastEndedCall = useCallback(() => {
    setLastEndedCall(null);
  }, []);

  const setOutboundCallId = useCallback((callId: string) => {
    setActiveCall((prev) => (prev ? { ...prev, callId } : prev));
  }, []);

  /**
   * Merge rich context from the Supabase Realtime broadcast into the incomingCall.
   * Called from App.tsx when the Realtime message arrives — may be before or after
   * the AT SDK fires its incomingcall event, so we merge rather than overwrite.
   */
  const setIncomingCallContext = useCallback((ctx: IncomingCallInfo) => {
    setIncomingCall((prev) => {
      // If the call has already been answered or ended (prev is null), do nothing.
      // Without this guard an async contact-lookup fetch completing after answer()
      // would resurrect incomingCall to non-null while sdkStatus is "incall",
      // creating inconsistent state that breaks the call audio.
      if (!prev) return prev;
      return {
        callId: ctx.callId ?? prev.callId ?? null,
        sessionId: ctx.sessionId,
        callerNumber: ctx.callerNumber || prev.callerNumber || "",
        contactName: ctx.contactName,
        contactId: ctx.contactId,
        contactTags: ctx.contactTags,
        assignedRepId: ctx.assignedRepId,
      };
    });
  }, []);

  return {
    sdkStatus,
    networkQuality,
    initError,
    activeCall,
    incomingCall,
    lastEndedCall,
    isMuted,
    isOnHold,
    call,
    answer,
    hangup,
    mute,
    unmute,
    hold,
    unhold,
    retryInit,
    setIncomingCallContext,
    clearLastEndedCall,
    setOutboundCallId,
  };
}
